import { forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useMotionValue, useTransform } from 'framer-motion'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
  ContextMenuShortcut,
} from '@/components/ui/context-menu'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  CheckCheck,
  ChevronDown,
  ChevronUp,
  Copy,
  Plus,
  Paperclip,
  RotateCcw,
  Trash2,
  Maximize2,
  Minimize2,
} from 'lucide-react'
import { PinIcon as Pin } from '@/components/ui/pin-icon'
import { Kbd, KbdGroup } from '@/components/ui/kbd'

import { toast } from 'sonner'

import { haptics } from '@/lib/haptics'
import { TodoItemHeader } from './todo-item-header'
import { actions } from '@/store'
import { useSortable, defaultAnimateLayoutChanges } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useIsMobile } from '@/hooks/use-mobile'
import { itemPresence, smoothSpring } from './motion-presets'
const ContextMenuShortcutKeys = ({ keys }) => (
  <ContextMenuShortcut className="opacity-60 pl-4">
    <KbdGroup>
      {keys.map((key) => (
        <Kbd key={key}>{key}</Kbd>
      ))}
    </KbdGroup>
  </ContextMenuShortcut>
)

const FileDropOverlay = ({ isDraggingOver }) => (
  <AnimatePresence>
    {isDraggingOver && (
      <motion.div
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        exit={{
          opacity: 0,
        }}
        className="absolute inset-0 z-50 rounded-lg bg-primary/10 border-2 border-dashed border-primary/40 flex items-center justify-center pointer-events-none"
      >
        <div className="bg-background/90 px-4 py-2 rounded-full shadow-lg border border-border/10 flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-primary" />
          <span className="text-xs font-bold text-primary">Drop to attach files</span>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
)
const TodoContextMenuItemLabel = ({ icon: Icon, label }) => (
  <>
    <Icon className="mr-2 h-3.5 w-3.5" />
    <span>{label}</span>
  </>
)

export const TodoItemContextMenu = memo(
  ({
    todo,
    isSelected,
    selectedCount,
    selectedIds,
    onDuplicate,
    onToggle,
    onUpdate,
    onDelete,
    onClearSelection,
  }) => {
    const isMultiSelect = isSelected && selectedCount > 1 && selectedIds?.size > 1

    const handleDuplicate = useCallback(() => {
      if (isMultiSelect) {
        selectedIds.forEach((id) => setTimeout(() => onDuplicate(id), 0))
      } else {
        setTimeout(() => onDuplicate(todo.id), 0)
      }
      onClearSelection?.()
    }, [onDuplicate, todo.id, isMultiSelect, selectedIds, onClearSelection])

    const handleToggle = useCallback(() => {
      if (isMultiSelect) {
        const targetCompleted = !todo.completed
        const targetStatus = targetCompleted ? 'done' : 'todo'
        selectedIds.forEach((id) => {
          window.record('task_updated', {
            action: 'batch_completion_toggle',
            completed: targetCompleted,
          })
          onUpdate(id, {
            completed: targetCompleted,
            status: targetStatus,
          })
        })
      } else {
        window.record(todo.completed ? 'task_uncompleted' : 'task_completed', {
          task_id: todo.id,
        })
        onToggle(todo.id)
      }
      onClearSelection?.()
    }, [onToggle, todo.id, todo.completed, isMultiSelect, selectedIds, onUpdate, onClearSelection])

    const handleExpand = useCallback(() => {
      const targetExpanded = !todo.isExpanded
      if (isMultiSelect) {
        selectedIds.forEach((id) =>
          onUpdate(id, {
            isExpanded: targetExpanded,
          }),
        )
      } else {
        onUpdate(todo.id, {
          isExpanded: targetExpanded,
        })
      }
      onClearSelection?.()
    }, [onUpdate, todo.id, todo.isExpanded, isMultiSelect, selectedIds, onClearSelection])

    const handleDelete = useCallback(() => {
      if (isMultiSelect) {
        selectedIds.forEach((id) => setTimeout(() => onDelete(id), 0))
      } else {
        setTimeout(() => {
          window.record('task_deleted', { task_id: todo.id })
          onDelete(todo.id)
        }, 0)
      }
      onClearSelection?.()
    }, [onDelete, todo.id, isMultiSelect, selectedIds, onClearSelection])

    const handleTogglePin = useCallback(() => {
      if (isMultiSelect) {
        const targetPinned = !todo.pinned
        selectedIds.forEach((id) => {
          window.record('task_pinned_toggle', { task_id: id, pinned: targetPinned })
          onUpdate(id, { pinned: targetPinned })
        })
      } else {
        window.record('task_pinned_toggle', { task_id: todo.id, pinned: !todo.pinned })
        actions.togglePinned(todo.id)
      }
      onClearSelection?.()
    }, [todo.id, todo.pinned, isMultiSelect, selectedIds, onUpdate, onClearSelection])

    return (
      <ContextMenuContent className="w-56">
        {isSelected && selectedCount && selectedCount > 1 && (
          <div className="px-2 py-1 text-xs text-muted-foreground font-medium bg-muted/30 rounded-lg mb-1 mx-1 text-center">
            {selectedCount} selected
          </div>
        )}
        <ContextMenuItem onSelect={handleToggle}>
          <TodoContextMenuItemLabel
            icon={todo.completed ? RotateCcw : CheckCheck}
            label={todo.completed ? 'Mark as todo' : 'Mark as done'}
          />
          <ContextMenuShortcutKeys keys={['D']} />
        </ContextMenuItem>

        <ContextMenuItem onSelect={handleExpand}>
          <TodoContextMenuItemLabel
            icon={todo.isExpanded ? Minimize2 : Maximize2}
            label={todo.isExpanded ? 'Collapse' : 'Open details'}
          />
          <ContextMenuShortcutKeys keys={['Space']} />
        </ContextMenuItem>

        <ContextMenuItem onSelect={handleTogglePin}>
          <TodoContextMenuItemLabel icon={Pin} label={todo.pinned ? 'Unpin' : 'Pin to top'} />
          <ContextMenuShortcutKeys keys={['P']} />
        </ContextMenuItem>

        <ContextMenuSeparator />

        <ContextMenuItem onSelect={handleDuplicate}>
          <TodoContextMenuItemLabel icon={Copy} label="Duplicate" />
          <ContextMenuShortcutKeys keys={['⌘', 'D']} />
        </ContextMenuItem>

        <ContextMenuSeparator />

        <ContextMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={handleDelete}
        >
          <TodoContextMenuItemLabel icon={Trash2} label="Delete task" />
          <ContextMenuShortcutKeys keys={['⌫']} />
        </ContextMenuItem>
      </ContextMenuContent>
    )
  },
)
TodoItemContextMenu.displayName = 'TodoItemContextMenu'

const hasSameBaseProps = (prev, next) =>
  [
    prev.todo === next.todo,
    prev.isEditing === next.isEditing,
    prev.isSelected === next.isSelected,
    prev.isDraggingOver === next.isDraggingOver,
    prev.viewMode === next.viewMode,
    prev.disableInteractions === next.disableInteractions,
    prev.list === next.list,
    prev.enabledFeatures === next.enabledFeatures,
    prev.showListBadge === next.showListBadge,
  ].every(Boolean)

const hasSameSelection = (prev, next) => {
  if (!next.isSelected) return true
  return prev.selectedCount === next.selectedCount && prev.selectedIds === next.selectedIds
}

const hasSameHandlers = (prev, next) =>
  [
    prev.onToggle === next.onToggle,
    prev.onUpdate === next.onUpdate,
    prev.onDelete === next.onDelete,
    prev.onSelect === next.onSelect,
    prev.onDuplicate === next.onDuplicate,
  ].every(Boolean)

const areTodoPropsEqual = (prev, next) =>
  hasSameBaseProps(prev, next) && hasSameSelection(prev, next) && hasSameHandlers(prev, next)

function TodoItemBase({
  todo,
  list,
  enabledFeatures,
  isSelected,
  isEditing,
  onToggle,
  onDelete,
  onUpdate,
  onSelect,
  onDuplicate,
  setEditingId,
  dragListeners,
  dragAttributes,
  selectedCount,
  selectedIds,
  disableInteractions,
  showListBadge,
  viewMode,
  onClearSelection,
  anyExpanded,
}) {
  const inputRef = useRef(null)
  const itemRef = useRef(null)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [subtaskText, setSubtaskText] = useState('')
  const [isCommentsExpanded, setCommentsExpanded] = useState(false)
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false)
  // Removed useSnapshot(store) to prevent global re-renders

  const getIsInteractiveTarget = useCallback((target) => {
    const closest = target.closest(
      'button, input, textarea, [role="button"], [role="menuitem"], [role="combobox"], [role="option"], .status-selector-container, [data-drag-handle], [role="dialog"], [role="popover"]',
    )
    // If it's an interactive element but NOT the task item itself, consider it interactive
    return closest && closest !== itemRef.current
  }, [])

  const handlePointerDown = useCallback(
    (e) => {
      if (getIsInteractiveTarget(e.target)) {
        e.stopPropagation()
      }
    },
    [getIsInteractiveTarget],
  )
  const handleKeyDown = useCallback(
    (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && !getIsInteractiveTarget(e.target)) {
        e.preventDefault()
        e.stopPropagation()
        onUpdate(
          todo.id,
          {
            isExpanded: true,
          },
          {
            multi: e.shiftKey,
          },
        )
      }
    },
    [onUpdate, todo.id, getIsInteractiveTarget],
  )
  const handleClick = useCallback(
    (e) => {
      if (e.metaKey || e.ctrlKey) {
        onSelect(todo.id, true)
        e.stopPropagation()
        return
      }

      if (!getIsInteractiveTarget(e.target)) {
        e.preventDefault()
        e.stopPropagation()
        onUpdate(
          todo.id,
          {
            isExpanded: true,
          },
          {
            multi: e.shiftKey,
          },
        )
      }
    },
    [onSelect, onUpdate, todo.id, getIsInteractiveTarget],
  )
  const handleFileDrop = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDraggingOver(false)
      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) {
        onUpdate(todo.id, {
          attachments: [...(todo.attachments || []), ...files.map((f) => f.name)],
        })
        toast.success(`Added ${files.length} attachment${files.length > 1 ? 's' : ''}`)
      }
    },
    [todo.attachments, todo.id, onUpdate],
  )
  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    if (e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy'
      setIsDraggingOver(true)
    }
  }, [])
  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setIsDraggingOver(false)
  }, [])

  const handleToggleComments = useCallback(() => setCommentsExpanded((prev) => !prev), [])
  const handleContextMenuOpenChange = useCallback((open) => {
    setIsContextMenuOpen(open)
    actions.setContextMenuOpen(open)
  }, [])
  useEffect(() => {
    if (isEditing) inputRef.current?.focus()
  }, [isEditing])
  const itemClassName = cn(
    'task-item group relative flex flex-col rounded-xl overflow-hidden bg-card text-card-foreground shadow-sm border border-transparent focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none cursor-pointer transition-all duration-200',
    !disableInteractions && !isSelected && 'hover:border-border/20 hover:shadow-md',
    disableInteractions && 'pointer-events-none',
    isSelected ? 'border-primary/40 shadow-xl ring-2 ring-primary/45' : '',
    isContextMenuOpen ? 'border-primary/60 bg-primary/5 ring-4 ring-primary/10 z-20' : '',
    isDraggingOver ? 'border-primary/50 bg-primary/5 shadow-2xl' : '',
    todo.completed && 'opacity-60 contrast-90 grayscale-[0.2]',
    todo.isExpanded ? 'bg-border text-card-foreground z-10 scale-[1.005] ' : 'bg-card/20',
    anyExpanded && !todo.isExpanded && 'opacity-30 contrast-75',
  )
  return (
    <ContextMenu onOpenChange={handleContextMenuOpenChange}>
      <ContextMenuTrigger asChild>
        <TodoItemContent
          isEditing={isEditing}
          itemRef={itemRef}
          todo={todo}
          handlePointerDown={handlePointerDown}
          handleKeyDown={handleKeyDown}
          handleClick={handleClick}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleFileDrop={handleFileDrop}
          itemClassName={itemClassName}
          isDraggingOver={isDraggingOver}
          list={list}
          setEditingId={setEditingId}
          inputRef={inputRef}
          onToggle={onToggle}
          onUpdate={onUpdate}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          dragListeners={dragListeners}
          dragAttributes={dragAttributes}
          disableInteractions={disableInteractions}
          showListBadge={showListBadge}
          enabledFeatures={enabledFeatures}
          viewMode={viewMode}
          subtaskText={subtaskText}
          setSubtaskText={setSubtaskText}
          isCommentsExpanded={isCommentsExpanded}
          handleToggleComments={handleToggleComments}
          isSelected={isSelected}
          onSelect={onSelect}
          anyExpanded={anyExpanded}
        />
      </ContextMenuTrigger>
      <TodoItemContextMenu
        todo={todo}
        isSelected={isSelected}
        selectedCount={selectedCount}
        selectedIds={selectedIds}
        onDuplicate={onDuplicate}
        onToggle={onToggle}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onClearSelection={onClearSelection}
      />
    </ContextMenu>
  )
}
const TodoItemContent = forwardRef(
  (
    {
      isEditing,
      itemRef,
      todo,
      handlePointerDown,
      handleKeyDown,
      handleClick,
      handleDragOver,
      handleDragLeave,
      handleFileDrop,
      itemClassName,
      isDraggingOver,
      list,
      setEditingId,
      inputRef,
      onToggle,
      onUpdate,
      onDuplicate,
      onDelete,

      dragListeners,
      dragAttributes,
      disableInteractions,
      showListBadge,
      enabledFeatures,
      viewMode,
      subtaskText,
      setSubtaskText,
      isCommentsExpanded,
      handleToggleComments,
      isSelected,
      onSelect,
      anyExpanded,
      ...props
    },
    ref,
  ) => {
    const isMobile = useIsMobile()
    const x = useMotionValue(0)
    const threshold = 80
    const [triggerAction, setTriggerAction] = useState(null)
    const bgRight = useTransform(
      x,
      [0, threshold],
      ['rgba(34, 197, 94, 0)', 'rgba(34, 197, 94, 1)'],
    )
    const bgLeft = useTransform(
      x,
      [-threshold, 0],
      ['rgba(239, 68, 68, 1)', 'rgba(239, 68, 68, 0)'],
    )
    const iconOpacity = useTransform(
      x,
      [-threshold, -threshold / 2, 0, threshold / 2, threshold],
      [1, 0, 0, 0, 1],
    )
    const iconScale = useTransform(x, [-threshold, 0, threshold], [1.1, 0.8, 1.1])
    const handleDrag = useCallback(
      (_e, info) => {
        if (info.offset.x > threshold) {
          if (triggerAction !== 'complete') {
            setTriggerAction('complete')
            haptics.impact()
          }
        } else if (info.offset.x < -threshold) {
          if (triggerAction !== 'delete') {
            setTriggerAction('delete')
            haptics.impact()
          }
        } else {
          setTriggerAction(null)
        }
      },
      [triggerAction, threshold],
    )
    const handleDragEnd = useCallback(
      (_e, info) => {
        if (info.offset.x > threshold) {
          haptics.success()
          onToggle(todo.id)
        } else if (info.offset.x < -threshold) {
          haptics.error()
          onDelete(todo.id)
        }
        x.set(0)
        setTriggerAction(null)
      },
      [todo.id, onToggle, onDelete, x, threshold],
    )
    const resolvedDisableInteractions = disableInteractions ?? false
    const resolvedIsExpanded = todo.isExpanded ?? false
    return (
      <div
        ref={ref}
        className={cn(
          'relative group overflow-hidden  transition-all duration-300',
          resolvedIsExpanded && 'bg-red',
        )}
        {...props}
      >
        {/* Swipe Backgrounds */}
        {isMobile && !disableInteractions && !isEditing && (
          <SwipeBackgrounds
            bgRight={bgRight}
            bgLeft={bgLeft}
            iconOpacity={iconOpacity}
            iconScale={iconScale}
          />
        )}

        <TodoItemBody
          todo={todo}
          list={list}
          isEditing={isEditing}
          setEditingId={setEditingId}
          inputRef={inputRef}
          onToggle={onToggle}
          onUpdate={onUpdate}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          dragListeners={dragListeners}
          dragAttributes={dragAttributes}
          disableInteractions={resolvedDisableInteractions}
          showListBadge={showListBadge}
          enabledFeatures={enabledFeatures}
          viewMode={viewMode}
          isSelected={isSelected}
          onSelect={onSelect}
          isMobile={isMobile}
          isDraggingOver={isDraggingOver}
          isExpanded={resolvedIsExpanded}
          isCommentsExpanded={isCommentsExpanded}
          setSubtaskText={setSubtaskText}
          subtaskText={subtaskText}
          handleToggleComments={handleToggleComments}
          itemRef={itemRef}
          itemClassName={itemClassName}
          x={x}
          handlePointerDown={handlePointerDown}
          handleKeyDown={handleKeyDown}
          handleClick={handleClick}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleFileDrop={handleFileDrop}
          handleDrag={handleDrag}
          handleDragEnd={handleDragEnd}
          isDraggingDisabled={disableInteractions || isEditing}
          anyExpanded={anyExpanded}
        />
      </div>
    )
  },
)
TodoItemContent.displayName = 'TodoItemContent'
export const TodoItem = memo(TodoItemBase, areTodoPropsEqual)
const SwipeBackgrounds = ({ bgRight, bgLeft, iconOpacity, iconScale }) => (
  <div className="absolute inset-0 z-0 pointer-events-none">
    <motion.div
      style={{
        backgroundColor: bgRight,
      }}
      className="absolute inset-y-0 left-0 w-full flex items-center pl-6"
    >
      <motion.div
        style={{
          opacity: iconOpacity,
          scale: iconScale,
        }}
      >
        <CheckCheck className="text-white h-5 w-5" />
      </motion.div>
    </motion.div>
    <motion.div
      style={{
        backgroundColor: bgLeft,
      }}
      className="absolute inset-y-0 right-0 w-full flex items-center justify-end pr-6"
    >
      <motion.div
        style={{
          opacity: iconOpacity,
          scale: iconScale,
        }}
      >
        <Trash2 className="text-white h-5 w-5" />
      </motion.div>
    </motion.div>
  </div>
)
const TodoItemBody = ({
  todo,
  list,
  isEditing,
  setEditingId,
  inputRef,
  onToggle,
  onUpdate,
  onDuplicate,
  onDelete,

  dragListeners,
  dragAttributes,
  disableInteractions,
  showListBadge,
  enabledFeatures,
  viewMode,
  isSelected,
  onSelect,
  isMobile,
  isDraggingOver,
  isExpanded,
  isCommentsExpanded,
  setSubtaskText,
  subtaskText,
  handleToggleComments,
  itemRef,
  itemClassName,
  x,
  handlePointerDown,
  handleKeyDown,
  handleClick,
  handleDragOver,
  handleDragLeave,
  handleFileDrop,
  handleDrag,
  handleDragEnd,
  isDraggingDisabled,
  anyExpanded,
}) => (
  <motion.div
    layout="position"
    layoutId={`todo-card-${todo.id}`}
    ref={itemRef}
    data-id={todo.id}
    role="button"
    tabIndex={0}
    aria-expanded={todo.isExpanded}
    onPointerDown={handlePointerDown}
    onKeyDown={handleKeyDown}
    onClick={handleClick}
    onDragOver={handleDragOver}
    onDragLeave={handleDragLeave}
    onDrop={handleFileDrop}
    drag={isMobile && !isDraggingDisabled ? 'x' : false}
    dragDirectionLock
    dragConstraints={{
      left: 0,
      right: 0,
    }}
    dragElastic={0.6}
    onDrag={handleDrag}
    onDragEnd={handleDragEnd}
    style={{
      x,
      touchAction: isMobile ? 'pan-y' : 'auto',
    }}
    transition={{
      ...smoothSpring,
      layout: smoothSpring,
    }}
    className={cn(itemClassName, 'relative z-10')}
  >
    <FileDropOverlay isDraggingOver={isDraggingOver} />

    <TodoItemHeader
      todo={todo}
      list={list}
      isEditing={isEditing}
      setEditingId={setEditingId}
      inputRef={inputRef}
      onToggle={onToggle}
      onUpdate={onUpdate}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
      dragListeners={dragListeners}
      dragAttributes={dragAttributes}
      disableInteractions={disableInteractions}
      showListBadge={showListBadge}
      enabledFeatures={enabledFeatures}
      viewMode={viewMode}
      isSelected={isSelected}
      onSelect={onSelect}
    />

    {/* Sidebar now handles expansion details */}
    <AnimatePresence initial={false} mode="popLayout"></AnimatePresence>
  </motion.div>
)
const InlineInsert = ({ onInsert, position }) => {
  const handleClick = useCallback(
    (e) => {
      e.stopPropagation()
      onInsert()
    },
    [onInsert],
  )
  return (
    <div
      className={cn(
        'absolute left-14 right-14 flex items-center justify-center z-20 pointer-events-none opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200',
        position === 'top' ? '-top-3' : '-bottom-3',
        'h-6',
      )}
    >
      <div className="absolute inset-x-4 h-[1px] bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5 rounded-full bg-background border border-border/10 text-muted-foreground/60 hover:text-primary hover:border-primary/50 hover:bg-primary/5 hover:scale-110 shadow-sm transition-all duration-200 pointer-events-auto z-10 p-0"
        onClick={handleClick}
      >
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  )
}
const animateLayoutChanges = (args) => {
  const { isSorting, wasDragging } = args

  // Since we update the DOM order in real-time, we shouldn't animate
  // the layout changes with CSS transitions, as it causes "double movement" glitches.
  if (isSorting) {
    return false
  }
  if (wasDragging) {
    return defaultAnimateLayoutChanges(args)
  }
  return defaultAnimateLayoutChanges(args)
}
const InlineInsertSlot = ({ show, position, onInsert }) =>
  show ? <InlineInsert position={position} onInsert={onInsert} /> : null
const isTodoGhosted = (isDragActive, draggingIds, todoId, isDragging) =>
  isDragActive && draggingIds.has(todoId) && !isDragging
const getInlineInsertVisibility = ({ disableInteractions, completed, isFirst }) => {
  if (disableInteractions || completed) {
    return {
      showInsertTop: false,
      showInsertBottom: false,
    }
  }
  return {
    showInsertTop: !isFirst,
    showInsertBottom: true,
  }
}
const getTransformStyle = (isDragging, isGhosted, transform) => {
  if (isDragging || isGhosted) return undefined
  return CSS.Translate.toString(transform)
}
const getPointerEventsStyle = (isDragging, isGhosted) =>
  isDragging || isGhosted ? 'none' : undefined
const getDragSizeStyle = (isDragActive, size) => {
  if (!isDragActive || !size) return {}
  return {
    height: size.height,
    minHeight: size.height,
    width: size.width,
  }
}
const getGhostStyle = (isGhosted) => {
  if (!isGhosted) return {}
  return {
    marginBottom: 0,
    marginTop: 0,
    padding: 0,
    borderWidth: 0,
    overflow: 'hidden',
  }
}
const getDragItemStyle = ({
  isDragging,
  isGhosted,
  isDragActive,
  transform,
  transition,
  size,
}) => ({
  // If dragging (or ghosted), we DON'T want the transform (cursor following) on the original item.
  // We want it to sit in the slot as a placeholder.
  transform: getTransformStyle(isDragging, isGhosted, transform),
  transition: isDragging ? undefined : transition,
  // Make it visible but styled as placeholder
  opacity: 1,
  visibility: 'visible',
  pointerEvents: getPointerEventsStyle(isDragging, isGhosted),
  touchAction: 'none',
  ...getDragSizeStyle(isDragActive, size),
  ...getGhostStyle(isGhosted),
})
export const SortableTodoItem = memo(
  ({
    todo,
    containerId,
    draggingIds,
    isDragActive,
    dragSizes,
    registerItem,
    children,
    onInlineInsert,
    onClearSelection,
    anyExpanded,
    ...props
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: todo.id,
      data: {
        type: 'item',
        containerId,
      },
      animateLayoutChanges,
    })
    const setRefs = useCallback(
      (node) => {
        setNodeRef(node)
        registerItem(todo.id, node)
      },
      [registerItem, setNodeRef, todo.id],
    )
    const size = dragSizes?.get(todo.id)
    const isGhosted = isTodoGhosted(isDragActive, draggingIds, todo.id, isDragging)
    const shouldRenderGhost = isDragging || isGhosted
    const { showInsertTop, showInsertBottom } = getInlineInsertVisibility({
      disableInteractions: props.disableInteractions,
      completed: todo.completed,
      isFirst: props.isFirst,
    })
    const handleInsertTop = useCallback(() => {
      onInlineInsert?.(todo.id, 'top')
    }, [onInlineInsert, todo.id])
    const handleInsertBottom = useCallback(() => {
      onInlineInsert?.(todo.id, 'bottom')
    }, [onInlineInsert, todo.id])
    const style = getDragItemStyle({
      isDragging,
      isGhosted,
      isDragActive,
      transform,
      transition: transition ?? undefined,
      size,
    })

    if (props.disableInteractions) {
      style.pointerEvents = 'none'
    }
    if (shouldRenderGhost) {
      return (
        <motion.div
          layout="position"
          ref={setRefs}
          style={style}
          transition={smoothSpring}
          className="relative z-[1] rounded-xl border-2 border-dashed border-primary/20 bg-primary/5"
          data-dnd-item
        />
      )
    }
    return (
      <motion.div
        layout="position"
        layoutId={`sortable-row-${todo.id}`}
        {...itemPresence}
        ref={setRefs}
        style={style}
        transition={{
          ...itemPresence.transition,
          layout: smoothSpring,
        }}
        className="group relative z-[1] my-0"
        data-dnd-item
        data-id={todo.id}
      >
        <InlineInsertSlot show={showInsertTop} position="top" onInsert={handleInsertTop} />
        <TodoItem
          todo={todo}
          {...props}
          dragAttributes={attributes}
          dragListeners={listeners}
          onClearSelection={onClearSelection}
          anyExpanded={anyExpanded}
        />
        <InlineInsertSlot show={showInsertBottom} position="bottom" onInsert={handleInsertBottom} />
        {children}
      </motion.div>
    )
  },
  (prev, next) => {
    return (
      prev.todo === next.todo &&
      prev.isDragActive === next.isDragActive &&
      prev.anyExpanded === next.anyExpanded &&
      prev.isFirst === next.isFirst &&
      prev.disableInteractions === next.disableInteractions &&
      prev.draggingIds === next.draggingIds &&
      prev.dragSizes === next.dragSizes &&
      prev.onInlineInsert === next.onInlineInsert &&
      prev.onClearSelection === next.onClearSelection
    )
  },
)

SortableTodoItem.displayName = 'SortableTodoItem'
